import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Link } from "react-router-dom";
import MyDatePicker from "../../widgets/MyDatePicker";

const WABAFormPreview = ({ formStructure }) => {
  const [currentScreen, setCurrentScreen] = useState(0);
  const [formData, setFormData] = useState({});
  const screens = useMemo(() => {
    return formStructure?.screens || [];
  }, [formStructure?.screens]);

  const handleInputChange = (screenId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [screenId]: {
        ...prev[screenId],
        [field]: value,
      },
    }));
  };

  const renderComponent = (component, screenId) => {
    switch (component.type) {
      case "TextHeading":
        return (
          <Typography variant="h4" gutterBottom>
            {component.text}
          </Typography>
        );

      case "TextSubheading":
        return (
          <Typography variant="h6" gutterBottom>
            {component.text}
          </Typography>
        );

      case "TextCaption":
        return (
          <Typography variant="caption" display="block" gutterBottom>
            {component.text}
          </Typography>
        );
      case "RichText":
      case "TextBody":
        return <Typography paragraph>{component.text}</Typography>;

      case "TextInput":
        return (
          <TextField
            fullWidth
            label={component.label}
            variant="outlined"
            value={formData[screenId]?.[component.name] || ""}
            onChange={(e) =>
              handleInputChange(screenId, component.name, e.target.value)
            }
            margin="normal"
          />
        );
      case "CalendarPicker":
        return <MyDatePicker label={component.label} />;

      case "RadioButtonsGroup":
        return (
          <FormControl component="fieldset" fullWidth margin="normal">
            <FormLabel component="legend">{component.label}</FormLabel>
            <RadioGroup
              value={formData[screenId]?.[component.name] || ""}
              onChange={(e) =>
                handleInputChange(screenId, component.name, e.target.value)
              }
            >
              {Array.isArray(component["data-source"]) &&
                component["data-source"].map((option) => (
                  <FormControlLabel
                    key={option.id}
                    value={option.id}
                    control={<Radio />}
                    label={option.title}
                  />
                ))}
            </RadioGroup>
          </FormControl>
        );

      case "CheckboxGroup":
        return (
          <FormControl component="fieldset" fullWidth margin="normal">
            <FormLabel component="legend">{component.label}</FormLabel>
            {Array.isArray(component["data-source"]) &&
              component["data-source"].map((option) => (
                <FormControlLabel
                  key={option.id}
                  control={
                    <Checkbox
                      checked={
                        formData[screenId]?.[component.name]?.includes(
                          option.id
                        ) || false
                      }
                      onChange={(e) => {
                        const newValue = e.target.checked
                          ? [
                              ...(formData[screenId]?.[component.name] || []),
                              option.id,
                            ]
                          : (formData[screenId]?.[component.name] || []).filter(
                              (id) => id !== option.id
                            );
                        handleInputChange(screenId, component.name, newValue);
                      }}
                    />
                  }
                  label={option.title}
                />
              ))}
          </FormControl>
        );

      case "Dropdown":
        return (
          <FormControl fullWidth margin="normal">
            <FormLabel>{component.label}</FormLabel>
            <Select
              value={formData[screenId]?.[component.name] || ""}
              onChange={(e) =>
                handleInputChange(screenId, component.name, e.target.value)
              }
              variant="outlined"
            >
              {Array.isArray(component["data-source"]) &&
                component["data-source"].map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.title}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        );

      case "EmbeddedLink":
        return (
          <Button
            variant="outlined"
            color="primary"
            onClick={() =>
              setCurrentScreen(
                screens.findIndex(
                  (s) => s.id === component["on-click-action"].next.name
                )
              )
            }
            sx={{ mt: 2 }}
          >
            {component.text}
          </Button>
        );

      case "Footer":
        return (
          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 3 }}
            onClick={() => console.log("Complete payload:", formData)}
          >
            {component.label}
          </Button>
        );

      case "If":
        const conditionValue = formData["SELECT_INSURANCE"]?.insurance;
        const conditionMet = conditionValue && conditionValue !== "";

        return (
          <Box sx={{ my: 2 }}>
            {conditionMet
              ? component.then.map((c, i) => (
                  <Box key={i}>{renderComponent(c, screenId)}</Box>
                ))
              : component.else.map((c, i) => (
                  <Box key={i}>{renderComponent(c, screenId)}</Box>
                ))}
          </Box>
        );

      case "Switch":
        const switchValue = formData["SELECT_INSURANCE"]?.insurance;
        return (
          <Box sx={{ my: 2 }}>
            {component.cases[switchValue]?.map((c, i) => (
              <Box key={i}>{renderComponent(c, screenId)}</Box>
            ))}
          </Box>
        );

      default:
        return (
          <Typography color="error">
            Unknown component: {component.type}
          </Typography>
        );
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, margin: "auto" }}>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center" }}>
        <Typography variant="h6" sx={{ mr: 2 }}>
          Screen Preview
        </Typography>
        <Select
          size="small"
          value={currentScreen}
          onChange={(e) => setCurrentScreen(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          {screens.map((screen, index) => (
            <MenuItem key={screen.id} value={index}>
              {screen.title} ({screen.id})
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Accordion variant="outlined" sx={{ borderRadius: 1 }} defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            {screens?.[currentScreen]?.title}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ p: 2 }}>
            {screens?.[currentScreen]?.layout.children.map(
              (component, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  {renderComponent(component, screens?.[currentScreen]?.id)}
                </Box>
              )
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      <Box sx={{ mt: 3, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
        <Typography variant="subtitle1">Form Data:</Typography>
        <pre>{JSON.stringify(formData, null, 2)}</pre>
      </Box>
    </Box>
  );
};
export default WABAFormPreview;
